import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SettlementDetailDialog } from "./SettlementDetailDialog";

const TONE = { borrador: "outline", aprobada: "secondary", pagada: "default" };

export const SettlementsTab = () => {
  const { t } = useT();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [estado, setEstado] = useState("all");
  const [detailId, setDetailId] = useState(null);
  const [corteFecha, setCorteFecha] = useState(new Date().toISOString().slice(0, 10));
  const [modalidad, setModalidad] = useState("quincenal");
  const [saltadas, setSaltadas] = useState([]);
  const [expanded, setExpanded] = useState({});

  const { data: settlements } = useQuery({
    queryKey: ["settlements", estado],
    queryFn: async () => (await api.get("/settlements", {
      params: { page_size: 100, estado: estado === "all" ? undefined : estado },
    })).data.items,
  });

  const generar = useMutation({
    mutationFn: () => api.post("/settlements/generate", { corte_fecha: corteFecha, modalidad }),
    onSuccess: ({ data }) => {
      toast.success(t("finance.settlements.generated", { n: data.creadas.length }));
      setSaltadas(data.saltadas || []);
      qc.invalidateQueries({ queryKey: ["settlements"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  return (
    <div data-testid="finance-settlements-tab" className="space-y-4">
      {can("finance", "create") && (
        <div className="flex flex-wrap items-end gap-2 rounded-[10px] border border-line bg-white p-3">
          <div>
            <label className="text-[11.5px] text-ink-tertiary">{t("finance.settlements.cutoff_date")}</label>
            <Input data-testid="cutoff-date-input" type="date" value={corteFecha} onChange={(e) => setCorteFecha(e.target.value)} className="h-8" />
          </div>
          <div>
            <label className="text-[11.5px] text-ink-tertiary">{t("finance.settlements.modality")}</label>
            <Select value={modalidad} onValueChange={setModalidad}>
              <SelectTrigger data-testid="modality-select" className="h-8 w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="quincenal">{t("finance.settlements.modalities.quincenal")}</SelectItem>
                <SelectItem value="semanal">{t("finance.settlements.modalities.semanal")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="generate-settlements-button" className="h-8 bg-brand hover:bg-brand-hover"
            disabled={generar.isPending} onClick={() => generar.mutate()}>
            <Plus className="mr-1.5 h-4 w-4" />{t("finance.settlements.generate")}
          </Button>
          <p className="text-[11.5px] text-ink-tertiary">{t("finance.settlements.generate_confirm")}</p>
        </div>
      )}

      <div className="flex justify-end">
        <Select value={estado} onValueChange={setEstado}>
          <SelectTrigger data-testid="settlement-status-filter" className="h-8 w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("finance.settlements.all_statuses")}</SelectItem>
            <SelectItem value="borrador">{t("finance.settlements.status.borrador")}</SelectItem>
            <SelectItem value="aprobada">{t("finance.settlements.status.aprobada")}</SelectItem>
            <SelectItem value="pagada">{t("finance.settlements.status.pagada")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!!saltadas.length && (
        <div data-testid="settlements-skipped-panel" className="space-y-2 rounded-[10px] border border-state-warn bg-state-warn-soft p-3">
          <p className="text-[12px] font-semibold text-ink">{t("finance.settlements.skipped_title", { n: saltadas.length })}</p>
          <ul className="space-y-1.5">
            {saltadas.map((s) => (
              <li key={s.sub_id} data-testid={`settlement-skipped-${s.sub_id}`} className="text-[12.5px]">
                <div className="flex items-center justify-between gap-2">
                  <span><span className="font-medium text-ink">{s.sub_nombre}</span>
                    <span className="text-ink-secondary"> — {s.motivo}</span></span>
                  {!!s.no_liquidables?.length && (
                    <button data-testid={`settlement-skipped-toggle-${s.sub_id}`}
                      onClick={() => setExpanded((e) => ({ ...e, [s.sub_id]: !e[s.sub_id] }))}
                      className="shrink-0 text-[11px] font-medium text-brand hover:text-brand-hover">
                      {expanded[s.sub_id] ? t("finance.settlements.collapse") : t("finance.settlements.expand")}
                    </button>
                  )}
                </div>
                {expanded[s.sub_id] && !!s.no_liquidables?.length && (
                  <ul className="mt-1.5 space-y-1 border-l border-state-warn-soft pl-3">
                    {s.no_liquidables.map((nl) => (
                      <li key={nl.work_order_id} data-testid={`settlement-skipped-wo-${nl.work_order_id}`} className="text-[12px]">
                        <a href={`/ordenes/${nl.work_order_id}`} target="_blank" rel="noreferrer" className="font-medium text-brand hover:text-brand-hover">
                          {nl.code}
                        </a>
                        <span className="text-ink-tertiary"> · {nl.property_nombre} · {formatMoney(nl.monto_cents)} · {nl.motivo}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-[10px] border border-line bg-white">
        {!settlements?.length ? (
          <p className="p-6 text-center text-[13px] text-ink-tertiary">{t("finance.settlements.empty")}</p>
        ) : (
          <table className="w-full text-[12.5px]" data-testid="settlements-table">
            <thead className="bg-surface-sunken text-[11px] uppercase tracking-[0.04em] text-ink-tertiary">
              <tr>
                <th className="px-3 py-2 text-left">{t("finance.settlements.code")}</th>
                <th className="px-3 py-2 text-left">{t("finance.settlements.sub")}</th>
                <th className="px-3 py-2 text-left">{t("finance.settlements.cutoff")}</th>
                <th className="px-3 py-2 text-left">{t("finance.settlements.net")}</th>
                <th className="px-3 py-2 text-left">Estado</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {settlements.map((s) => (
                <tr key={s.id} className="border-b border-line cursor-pointer hover:bg-surface-sunken"
                  data-testid={`settlement-row-${s.id}`} onClick={() => setDetailId(s.id)}>
                  <td className="px-3 py-1.5 font-medium">{s.code}</td>
                  <td className="px-3 py-1.5">{s.sub_nombre}</td>
                  <td className="px-3 py-1.5">{formatDate(s.corte_fecha)}</td>
                  <td className="px-3 py-1.5 tabular-nums">{formatMoney(s.neto_a_pagar_cents)}</td>
                  <td className="px-3 py-1.5"><Badge variant={TONE[s.estado]}>{t(`finance.settlements.status.${s.estado}`)}</Badge></td>
                  <td className="px-3 py-1.5 text-right"><Eye className="ml-auto h-3.5 w-3.5 text-ink-tertiary" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detailId && <SettlementDetailDialog id={detailId} open onOpenChange={(v) => !v && setDetailId(null)} />}
    </div>
  );
};
